import type { IconName } from "@salestrak/ui";

import { SEARCH_SHORTCUT } from "#app/components/global-search/global-search-button";
import { IS_MAC, shortcutLabel } from "#app/lib/platform";
import { SIDEBAR_TOGGLE_SHORTCUT } from "#app/components/navigation/collapse-sidebar-button";
import {
  goToDestinationsForViewer,
  MODULE_HOTKEY_LEAD,
  type ModuleTier,
} from "#app/components/navigation/module-rail/tiles";
import { INBOX_FILTERS } from "#app/hooks/inbox/inbox-filters";
import {
  MAP_CENTER_SHORTCUT,
  MAP_PAN_SHORTCUT,
  MAP_ZOOM_IN_SHORTCUT,
  MAP_ZOOM_OUT_SHORTCUT,
} from "#app/components/map/map-shortcuts";
import { COMPACT_MODE_SHORTCUT } from "#app/lib/hooks/use-compact-mode-hotkey";
import { CREATE_PROJECT_SHORTCUT } from "#app/lib/hooks/use-create-project-hotkey";
import {
  CLEAR_FILTERS_SHORTCUT,
  FILTER_PANEL_SHORTCUT,
} from "#app/lib/hooks/use-filter-hotkeys";
import { LOGOUT_SHORTCUT } from "#app/lib/hooks/use-logout-hotkey";
import { SAVE_VIEW_SHORTCUT } from "#app/lib/hooks/use-save-view-hotkey";
import { VIEW_SEARCH_SHORTCUT } from "#app/lib/hooks/use-slash-search";
import { UNDO_SHORTCUT } from "#app/lib/undo-shortcut";

/** Keycaps for the chord that opens the reference from anywhere (⌘/ on Mac,
 *  Ctrl+/ elsewhere — the binding fires on metaKey OR ctrlKey). */
export const KEYBOARD_SHORTCUTS_SHORTCUT = IS_MAC ? ["⌘", "/"] : ["Ctrl", "/"];

/** The chord as one run of text, for `MenuShortcut` slots. */
export const KEYBOARD_SHORTCUTS_SHORTCUT_LABEL = shortcutLabel(KEYBOARD_SHORTCUTS_SHORTCUT);

/** One chord: the keycaps pressed together (or, with `sequence`, in order). */
export type ShortcutKeys = readonly string[];

export type ShortcutEntry = {
  /** What the chord does, as the row's label. */
  label: string;
  /** Alternatives, rendered "A or B". Every entry has at least one. */
  chords: readonly ShortcutKeys[];
  /** The keys are pressed one after another ("G then T"), not together. */
  sequence?: boolean;
  /** A qualifier the label cannot carry: where the pointer has to be, what
   *  state the screen has to be in. Rare — most rows need none, and a note
   *  that restates the label or an obvious precondition is noise. */
  note?: string;
};

/** One cluster of rows inside a section. A section with a single genre keeps
 *  one unlabeled group; a label (and optional description) marks a genuinely
 *  different kind of chord below. */
export type ShortcutGroup = {
  label?: string;
  description?: string;
  entries: readonly ShortcutEntry[];
};

export type ShortcutSection = {
  id: string;
  label: string;
  icon: IconName;
  /** Where these chords work — the one-line scope under the section title. */
  description: string;
  groups: readonly ShortcutGroup[];
  /** Set when the screen these chords work on belongs to a member module
   *  (`ModuleTier`): the section is left out of a Guest's reference, since
   *  the screen itself is not theirs to open. */
  tier?: ModuleTier;
  /** Set when the screen these chords work on sits behind the Automations
   *  door (feature flag and permission): left out for a viewer who cannot
   *  open it, so the reference never lists chords for a screen they lack. */
  requiresAutomations?: boolean;
};

// The modifier glyphs read right on a Mac; elsewhere they are spelled out. The
// bindings themselves fire on metaKey OR ctrlKey, so both labels are true.
const MOD = IS_MAC ? "⌘" : "Ctrl";
const SHIFT = IS_MAC ? "⇧" : "Shift";

/**
 * Every shortcut the reference lists, grouped by where it works.
 *
 * What earns a row: a chord a person has to LEARN — one with no visible
 * affordance, or one the UI advertises in a tooltip and nowhere else. What
 * does not: conventions every app shares (Escape closes an overlay, Enter
 * commits a field, arrows walk a list or menu), the micro-interactions inside
 * library controls (arrow keys on a resize handle or a color picker), and
 * bindings the app cannot reach. Listing those would bury the eleven chords
 * worth knowing under fifty that anyone would guess.
 *
 * Wherever a binding already exports its keycaps for a tooltip, the row
 * derives from that constant — the reference cannot drift from the chrome.
 */
const GLOBAL_SECTION: ShortcutSection = {
  id: "global",
  label: "Global",
  icon: "globe",
  description: "Available on every screen of the workspace.",
  groups: [
    {
      entries: [
        { label: "Search or run a command", chords: [SEARCH_SHORTCUT] },
        { label: "Search the current view", chords: [VIEW_SEARCH_SHORTCUT] },
        { label: "Filter the current view", chords: [FILTER_PANEL_SHORTCUT] },
        { label: "Clear the view's filters", chords: [CLEAR_FILTERS_SHORTCUT] },
        { label: "Save the view's changes", chords: [SAVE_VIEW_SHORTCUT] },
        { label: "Create a Project", chords: [CREATE_PROJECT_SHORTCUT] },
        {
          label: "Undo a delete",
          chords: [UNDO_SHORTCUT],
          note: "While its toast is showing",
        },
        { label: "Show or hide the sidebar", chords: [SIDEBAR_TOGGLE_SHORTCUT] },
        { label: "Compact navigation", chords: [COMPACT_MODE_SHORTCUT] },
        { label: "Keyboard shortcuts", chords: [KEYBOARD_SHORTCUTS_SHORTCUT] },
        { label: "Log out", chords: [LOGOUT_SHORTCUT] },
      ],
    },
  ],
};

/** The section a fresh open lands on. */
export const DEFAULT_SHORTCUT_SECTION = GLOBAL_SECTION;

/**
 * The reference as one viewer reads it. The catalog is the same for everyone
 * except where a chord leads somewhere the viewer cannot go: a share-only
 * viewer's Navigation section lists no member module (the hook ignores those
 * letters for them), and the sections for member-module screens are left out.
 */
export function shortcutSectionsForViewer(
  viewerIsShareOnly: boolean,
  canOpenAutomations = true,
): readonly ShortcutSection[] {
  const navigation: ShortcutSection = {
    id: "navigation",
    label: "Navigation",
    icon: "navigation",
    description: `Press ${MODULE_HOTKEY_LEAD}, then a letter, to jump to a module or screen.`,
    groups: [
      {
        // Derived from the chord registries (tiles, then screens): a module
        // hidden from the rail is still reachable by chord, so every
        // registered letter the viewer may use is listed — and a screen row
        // (Inbox, Settings) is the only place its chord is advertised, since
        // neither has a rail tile whose tooltip could carry it.
        entries: goToDestinationsForViewer(viewerIsShareOnly).map((destination) => ({
          label: `Go to ${destination.label}`,
          chords: [[MODULE_HOTKEY_LEAD, destination.hotkey]],
          sequence: true,
        })),
      },
    ],
  };
  return [GLOBAL_SECTION, navigation, ...SCREEN_SECTIONS].filter(
    (section) =>
      !(viewerIsShareOnly && section.tier === "member") &&
      !(section.requiresAutomations && !canOpenAutomations),
  );
}

// Sections for chords that work on one screen, in reference order.
const SCREEN_SECTIONS: readonly ShortcutSection[] = [
  {
    id: "search",
    label: "Search",
    icon: "search",
    description: "While the search dialog is open.",
    groups: [
      {
        entries: [
          { label: "Switch to commands", chords: [["/"]] },
          {
            label: "Open the highlighted result in a new tab",
            chords: [[MOD, "Enter"]],
          },
          { label: "Copy the highlighted result's link", chords: [[MOD, "C"]] },
          { label: "Clear the search", chords: [["Esc"]] },
        ],
      },
    ],
  },
  {
    id: "inbox",
    label: "Inbox",
    icon: "inbox",
    description: "On the Inbox screen.",
    groups: [
      {
        label: "Tabs & filters",
        entries: [
          { label: "Show all notifications", chords: [[SHIFT, "A"]] },
          { label: "Show cleared notifications", chords: [[SHIFT, "C"]] },
          // Derived from the filter registry, which owns each filter's digit.
          ...INBOX_FILTERS.filter((f) => f.hotkey !== null).map((f) => ({
            label: `Filter: ${f.label}`,
            chords: [[SHIFT, String(f.hotkey)]],
          })),
        ],
      },
      {
        label: "Notifications",
        entries: [
          {
            label: "Clear every notification on the tab",
            chords: [[SHIFT, "E"]],
          },
          {
            label: "Mark read or unread",
            chords: [["U"]],
            note: "With the pointer over a notification",
          },
          {
            label: "Clear or restore",
            chords: [["E"]],
            note: "With the pointer over a notification",
          },
        ],
      },
    ],
  },
  {
    id: "editor",
    label: "Text editor",
    icon: "text-cursor-input",
    description: "In comments, notes and any rich-text field.",
    groups: [
      {
        label: "Insert",
        entries: [
          { label: "Insert a block or run a command", chords: [["/"]] },
          { label: "Mention a person", chords: [["@"]] },
        ],
      },
      {
        label: "Formatting",
        entries: [
          { label: "Bold", chords: [[MOD, "B"]] },
          { label: "Italic", chords: [[MOD, "I"]] },
          { label: "Underline", chords: [[MOD, "U"]] },
          { label: "Strikethrough", chords: [[MOD, SHIFT, "S"]] },
          { label: "Inline code", chords: [[MOD, "E"]] },
          { label: "Bulleted list", chords: [[MOD, SHIFT, "8"]] },
          { label: "Numbered list", chords: [[MOD, SHIFT, "7"]] },
          { label: "Indent or outdent", chords: [["Tab"], [SHIFT, "Tab"]] },
        ],
      },
      {
        label: "History",
        entries: [
          { label: "Undo", chords: [[MOD, "Z"]] },
          { label: "Redo", chords: [[MOD, SHIFT, "Z"]] },
        ],
      },
    ],
  },
  {
    id: "automations",
    label: "Automations",
    icon: "zap",
    requiresAutomations: true,
    description: "On the automation canvas.",
    groups: [
      {
        label: "Canvas",
        entries: [
          { label: "Fit the whole automation in view", chords: [["1"]] },
          { label: "Reset zoom to 100%", chords: [["0"]] },
          { label: "Zoom in", chords: [["+"]] },
          { label: "Zoom out", chords: [["-"]] },
          { label: "Lay out left to right", chords: [["H"]] },
          { label: "Lay out top to bottom", chords: [["V"]] },
        ],
      },
      {
        label: "History",
        entries: [
          { label: "Undo", chords: [[MOD, "Z"]] },
          { label: "Redo", chords: [[MOD, SHIFT, "Z"]] },
        ],
      },
    ],
  },
  {
    id: "dashboards",
    tier: "member",
    label: "Dashboards",
    icon: "bar-chart",
    description: "While arranging widgets on a dashboard you can edit.",
    groups: [
      {
        label: "Layout",
        entries: [
          {
            label: "Pick up or drop the focused widget",
            chords: [["Space"], ["Enter"]],
          },
          { label: "Move the picked-up widget", chords: [["↑", "↓", "←", "→"]] },
        ],
      },
      {
        label: "History",
        entries: [
          { label: "Undo a layout change", chords: [[MOD, "Z"]] },
          { label: "Redo a layout change", chords: [[MOD, SHIFT, "Z"]] },
        ],
      },
    ],
  },
  {
    id: "map",
    tier: "member",
    label: "Map",
    icon: "map-pin",
    description: "On the Live Map, with the map focused.",
    groups: [
      {
        // Every row derives its keycaps from the same constants the map
        // dock's tooltips read (map-shortcuts.ts). Zoom and pan are Google
        // Maps' own bindings and need the canvas focused — the section's
        // scope line carries that once for all rows.
        entries: [
          { label: "Center on my location", chords: [MAP_CENTER_SHORTCUT] },
          { label: "Zoom in", chords: [MAP_ZOOM_IN_SHORTCUT] },
          { label: "Zoom out", chords: [MAP_ZOOM_OUT_SHORTCUT] },
          { label: "Pan the map", chords: [MAP_PAN_SHORTCUT] },
        ],
      },
    ],
  },
  {
    id: "org-chart",
    tier: "member",
    label: "Org chart",
    icon: "network",
    description: "On a team's org chart.",
    groups: [
      {
        entries: [
          { label: "Fit the whole chart in view", chords: [["1"]] },
          { label: "Reset zoom to 100%", chords: [["0"]] },
          { label: "Zoom in", chords: [["+"]] },
          { label: "Zoom out", chords: [["-"]] },
        ],
      },
    ],
  },
];

/** Every section, as a non-Guest reads it — the full catalog. */
export const SHORTCUT_SECTIONS: readonly ShortcutSection[] =
  shortcutSectionsForViewer(false);


/** A row key stable enough to scroll to and flash: labels are unique within
 *  their section (asserted by the catalog test). */
export function shortcutEntryKey(sectionId: string, entry: ShortcutEntry): string {
  return `${sectionId}:${entry.label}`;
}

export type ShortcutSearchResult = {
  section: ShortcutSection;
  /** The section's entries whose label or note matched. Empty when only the
   *  section (or one of its group labels) matched — the section row alone is
   *  the result then. */
  entries: readonly ShortcutEntry[];
};

/**
 * The sidemenu search: every section that contains the query — in its own
 * label, a group label, or an entry's label/note — with the matching entries
 * listed as jump targets beneath it. Case-insensitive substring; label order
 * is catalog order, so results read in the same order as the nav.
 */
export function searchShortcutSections(
  query: string,
  sections: readonly ShortcutSection[] = SHORTCUT_SECTIONS,
): ShortcutSearchResult[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const results: ShortcutSearchResult[] = [];
  for (const section of sections) {
    const sectionHit =
      section.label.toLowerCase().includes(q) ||
      section.groups.some((g) => g.label?.toLowerCase().includes(q));
    const entries = section.groups.flatMap((g) =>
      g.entries.filter(
        (e) =>
          e.label.toLowerCase().includes(q) ||
          e.note?.toLowerCase().includes(q),
      ),
    );
    if (sectionHit || entries.length > 0) results.push({ section, entries });
  }
  return results;
}
